/**
 * Agrupamentos e nomes usados no editor de perfis de acesso.
 */

export type GrupoDeItens<T> = { id: string; title: string; itens: T[] };

export const GRUPO_OUTROS = '__outros__';

/**
 * Agrupa `itens` pela chave, na ordem de `ordem` (ex.:
 * `config.blocks.groupBlocksOrder`). Grupos fora da ordem vêm depois, em ordem
 * alfabética; itens sem grupo vão para "Outros", no fim.
 */
export const agruparPor = <T>(
  itens: T[],
  chave: (item: T) => string | undefined,
  ordem: { id: string; title: string }[] = [],
  tituloOutros = 'Outros',
): GrupoDeItens<T>[] => {
  const grupos = new Map<string, T[]>();
  itens.forEach((item) => {
    const id = chave(item) || GRUPO_OUTROS;
    grupos.set(id, [...(grupos.get(id) ?? []), item]);
  });

  const titulos = new Map(ordem.map((grupo) => [grupo.id, grupo.title]));
  const posicao = (id: string) => {
    if (id === GRUPO_OUTROS) return Number.MAX_SAFE_INTEGER;
    const indice = ordem.findIndex((grupo) => grupo.id === id);
    return indice === -1 ? ordem.length : indice;
  };

  return [...grupos.entries()]
    .map(([id, lista]) => ({
      id,
      title: id === GRUPO_OUTROS ? tituloOutros : titulos.get(id) ?? id,
      itens: lista,
    }))
    .sort(
      (a, b) =>
        posicao(a.id) - posicao(b.id) ||
        a.title.localeCompare(b.title, 'pt-BR'),
    );
};

/**
 * Nome legível de um layout: o de `layoutViewsNamesMapping` (traduzido por
 * `traduzir`) ou, sem entrada, o id sem o sufixo `_view`.
 */
export const nomeDoLayout = (
  layout: string,
  nomes: Record<string, string> | undefined,
  traduzir: (texto: string) => string = (texto) => texto,
) => {
  const nome = nomes?.[layout];
  if (nome) return traduzir(nome);
  const semSufixo = layout.replace(/_view$/, '').replace(/[_-]+/g, ' ');
  return semSufixo.charAt(0).toUpperCase() + semSufixo.slice(1);
};
